import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useForm } from "react-hook-form";
import { NotificationManager } from "react-notifications";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";
import { CierresMensualesProduccionApi } from "../../../api/CierresMensualesProduccionApi";
import { usePadresProductoTerminado } from "../../../../hooks/usePadresProductoTerminado";

const STORAGE_KEY = "cierre_mensual_produccion_filtros";

// El cierre se pide por producto padre y fecha de corte, y en el primer cierre
// del producto padre también por el periodo inicial de inventario. Desde dónde
// arranca la producción no se elige: es su inicio, su reanudación o el día
// siguiente al último corte, y se muestra para que se entienda la fecha de corte
// mínima
export const useFiltrosCierreProduccion = ({ onConsultar }) => {
    const filtrosGuardados =
        JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};

    const {
        control: formControl,
        formState: { errors: formErrors },
        handleSubmit: formHandleSubmit,
        watch: formWatch,
        setValue: formSetValue,
    } = useForm({
        defaultValues: {
            padre_producto_terminado:
                filtrosGuardados.padre_producto_terminado || null,
            periodo_inicial: filtrosGuardados.periodo_inicial || null,
            fecha_corte: filtrosGuardados.fecha_corte
                ? moment(filtrosGuardados.fecha_corte)
                : null,
        },
    });

    const padreProductoTerminado = formWatch("padre_producto_terminado");
    const periodoInicial = formWatch("periodo_inicial");
    const fechaCorte = formWatch("fecha_corte");

    const padreProductoTerminadoId = padreProductoTerminado
        ? padreProductoTerminado.id
        : null;

    const { getSiguienteCierre, cargando: cargandoSiguienteCierre } =
        CierresMensualesProduccionApi();

    // si al producto padre le toca su primer cierre, desde cuándo tiene
    // producción, su último corte y los periodos que puede tomar como inicial
    const [siguienteCierre, setSiguienteCierre] = useState(null);

    // al cambiar rápido de producto padre solo cuenta la última respuesta
    const ultimaConsultaSiguienteCierre = useRef(0);

    const loadPadresProductoTerminado = usePadresProductoTerminado();

    // el periodo inicial se manda si se eligió; el backend solo lo usa en el
    // primer cierre del producto padre
    const construirParams = (filtros) => {
        const params = {
            padre_producto_terminado_id: filtros.padre_producto_terminado.id,
            fecha_corte: moment(filtros.fecha_corte).format("YYYY-MM-DD"),
        };

        if (filtros.periodo_inicial) {
            params.periodo_inicial_id = filtros.periodo_inicial.id;
        }

        return params;
    };

    const GetSiguienteCierre = async () => {
        const consulta = ++ultimaConsultaSiguienteCierre.current;

        setSiguienteCierre(null);

        if (!padreProductoTerminadoId) return;

        try {
            const data = await getSiguienteCierre({
                padre_producto_terminado_id: padreProductoTerminadoId,
            });

            if (consulta !== ultimaConsultaSiguienteCierre.current) return;

            setSiguienteCierre(data);

            if (!data.es_primer_cierre) {
                formSetValue("periodo_inicial", null);
            }
        } catch (error) {
            if (consulta !== ultimaConsultaSiguienteCierre.current) return;

            NotificationManager.error(
                getErrorFromResponse(
                    error,
                    "Error al obtener el último cierre del producto padre",
                ),
                "Error",
                3000,
            );
        }
    };

    useEffect(() => {
        GetSiguienteCierre();
    }, [padreProductoTerminadoId]);

    useEffect(() => {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                padre_producto_terminado: padreProductoTerminado,
                periodo_inicial: periodoInicial,
                fecha_corte: fechaCorte,
            }),
        );
    }, [padreProductoTerminado, periodoInicial, fechaCorte]);

    // al entrar se consulta de una vez si los filtros ya venían guardados
    useEffect(() => {
        if (fechaCorte && padreProductoTerminado) {
            onConsultar(
                construirParams({
                    padre_producto_terminado: padreProductoTerminado,
                    periodo_inicial: periodoInicial,
                    fecha_corte: fechaCorte,
                }),
            );
        }
    }, []);

    // La fecha de corte mínima se conoce en el primer cierre al elegir el periodo
    // inicial, y en los demás al elegir el producto padre, porque es la
    // reanudación o el día siguiente al último corte. Mientras no se conozca, o
    // si no hay producción desde el último cierre, la fecha de corte no se
    // muestra
    const obtenerFechaCorteMinima = () => {
        if (!siguienteCierre) return null;

        if (!siguienteCierre.es_primer_cierre) {
            return siguienteCierre.fecha_corte_minima
                ? moment(siguienteCierre.fecha_corte_minima)
                : null;
        }

        const periodo =
            periodoInicial &&
            siguienteCierre.periodos_iniciales.find(
                (opcion) => opcion.id === periodoInicial.id,
            );

        return periodo ? moment(periodo.fecha_corte_minima) : null;
    };

    // una vez registrado el cierre, esa fecha de corte ya quedó cerrada y al
    // volver a entrar se consultaría un tramo vacío
    const limpiarFiltrosGuardados = () => {
        sessionStorage.removeItem(STORAGE_KEY);
    };

    return {
        formControl,
        formErrors,
        formSetValue,
        loadPadresProductoTerminado,
        limpiarFiltrosGuardados,

        padreProductoTerminado,
        siguienteCierre,
        fechaCorteMinima: obtenerFechaCorteMinima(),
        cargandoSiguienteCierre,

        consultarReporte: formHandleSubmit((filtros) =>
            onConsultar(construirParams(filtros)),
        ),
    };
};
